import { atom, useAtom, useSetAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useEffect } from "react";

import { useActiveAccount } from "@/core/auth/activeAccount";
import { useShiftGate } from "@/panels/conversations/useShiftGate";
import { emptyLayout, type Layout } from "@/shell/layout";
import {
  KIOSK_SHIFT_GATED_ROLES,
  type KioskRole,
} from "@/shell/render/kioskRoles";

import { debouncedStorageFor } from "./debouncedStorage";
import {
  historyByWorkspaceAtom,
  pushHistorySnapshot,
  skipHistoryAtom,
} from "./history";
import {
  nextKiosk,
  buildInitialWorkspaces,
  DEFAULT_ACTIVE_WORKSPACE_ID,
} from "./presets";
import type { Workspace } from "./types";

/* ============================================================ */
/* Persistent atoms (localStorage via atomWithStorage)            */
/* ============================================================ */

/**
 * Tous les workspaces (presets + customs). Persist en localStorage sous
 * `cockpit:workspaces:v1`. Initial : 3 presets construits par
 * `buildInitialWorkspaces()` (cf presets.ts).
 *
 * Bump le suffixe `:v1` si la shape de Workspace ou Layout evolue de facon
 * incompatible — l'ancienne entree sera ignoree et l'init re-applique.
 */
export const workspacesAtom = atomWithStorage<Record<string, Workspace>>(
  "cockpit:workspaces:v1",
  buildInitialWorkspaces(),
  debouncedStorageFor<Record<string, Workspace>>()
);

/** ID du workspace actif. Persist (debounced). */
export const activeWorkspaceIdAtom = atomWithStorage<string>(
  "cockpit:active-workspace:v1",
  DEFAULT_ACTIVE_WORKSPACE_ID,
  debouncedStorageFor<string>()
);

/* ============================================================ */
/* Wizard layout override                                         */
/* ============================================================ */

/**
 * Override transient du layout cockpit, set par
 * `CreatorOnboardingWizard` pendant les steps `content` / `enrichment`
 * pour forcer un single-panel Vault visible derriere son chrome slim.
 *
 * Quand cet atom est set (non-null), `effectiveLayoutAtom` retourne sa
 * valeur a la place du layout du workspace actif. Quand le wizard se ferme
 * (creator complete) ou switch sur un autre step, l'atom est remis a null
 * et l'user retrouve son workspace habituel intact — pas de modification
 * persistante de ses panels.
 *
 * NOT persisted : in-memory uniquement (atom() simple, pas
 * atomWithStorage). Un reload du browser pendant l'onboarding remet le
 * wizard a zero qui re-injecte le layout au mount.
 */
export const wizardLayoutOverrideAtom = atom<Layout | null>(null);

/**
 * Override transient du layout pour le KIOSQUE. Set par `useKioskLayout` au
 * mount d'un rôle en kiosque avec son layout FIXE (cf `buildKiosk` +
 * `KIOSK_TABS`), remis à null pour tout autre rôle. Même
 * mécanique que `wizardLayoutOverrideAtom` : `layoutAtom` le lit à la place du
 * workspace, et les writes (ouverture d'un onglet fan, resize) y restent
 * IN-MEMORY → le workspace persistant du chatteur n'est jamais touché, et un
 * reload repart toujours du kiosk déterministe (aucune conv ouverte).
 *
 * NON persisté (atom() simple). Le wizard prime sur le kiosk (précédence
 * défensive — un rôle en kiosque ne déclenche pas l'onboarding créatrice de
 * toute façon).
 */
export const kioskLayoutOverrideAtom = atom<Layout | null>(null);

/* ============================================================ */
/* Derived atoms                                                  */
/* ============================================================ */

/**
 * Workspace actif (objet complet). Fallback sur n'importe quel workspace
 * si l'ID actif pointe sur un workspace supprime (defensive).
 */
export const activeWorkspaceAtom = atom<Workspace | null>((get) => {
  const map = get(workspacesAtom);
  const id = get(activeWorkspaceIdAtom);
  return map[id] ?? Object.values(map)[0] ?? null;
});

/**
 * Layout actif — derived read+write sur le workspace courant.
 *
 *   - Read : retourne le layout du workspace actif (ou empty si aucun)
 *   - Write : update le layout du workspace actif + push une snapshot
 *     pre-mutation dans l'historique pour Ctrl+Z. Persistence
 *     automatique via atomWithStorage sur workspacesAtom.
 *
 * Toutes les mutations (resizeSplit, setActiveTab, addPanel, removePanel,
 * moveTab, splitOnEdge, setPanelView, setPanelParams, applyDrop) ecrivent
 * ici via setLayout — donc toutes sont undoable via Ctrl+Z.
 *
 * History throttle 250ms (cf history.ts) : un drag-resize qui spam des
 * mutations a 60Hz devient UN seul undo step.
 */
/**
 * Heal-on-mount : re-sync les `name` + `description` des workspaces preset
 * depuis `buildInitialWorkspaces()` vers le state localStorage. Ne touche pas
 * aux layouts, aux timestamps, ni aux workspaces custom (`isPreset: false`).
 *
 * Utile quand on rename un preset dans le code (ex: "Empty" → "Default") :
 * sans cette migration, les browsers existants garderaient l'ancien nom
 * indefiniment puisque atomWithStorage a fige la valeur initiale.
 */
export function usePresetMetadataMigration() {
  const [workspaces, setWorkspaces] = useAtom(workspacesAtom);

  useEffect(() => {
    const initial = buildInitialWorkspaces();
    let dirty = false;
    const next: Record<string, Workspace> = { ...workspaces };
    for (const [id, canonical] of Object.entries(initial)) {
      const cur = workspaces[id];
      if (!cur || !cur.isPreset) continue;
      if (cur.name !== canonical.name || cur.description !== canonical.description) {
        next[id] = { ...cur, name: canonical.name, description: canonical.description };
        dirty = true;
      }
    }
    if (dirty) setWorkspaces(next);
    // Run once at mount — voluntarily skipping deps to avoid an infinite loop
    // (setWorkspaces would reschedule otherwise).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Kiosque : injecte le layout FIXE du rôle comme override in-memory tant que
 * le caller est en kiosque, et le retire sinon (ou au démontage). Le VRAI
 * shell (`LayoutRenderer`) rend alors ces panneaux exactement comme pour un
 * rôle libre — plus de surface réinventée. Le workspace persistant n'est
 * jamais touché (cf `kioskLayoutOverrideAtom`).
 *
 * ⚠️ Le shift ne concerne QUE les rôles de `KIOSK_SHIFT_GATED_ROLES` (le
 * chatteur) : chez lui, l'onglet Conversations n'existe que si un créneau lui
 * est attribué. Le manager l'a toujours — il passe outre la fenêtre de shift,
 * la lui opposer n'aurait aucun sens, et c'est précisément l'onglet pour lequel
 * Kayla le recrute.
 *
 * Chez le chatteur donc : l'onglet Conversations n'existe QUE si un shift lui
 * est attribué.
 * On lit ça sur le shift gate (`hasAssignedShift`), qui est une query Convex
 * réactive : le jour où Kayla lui pose un créneau, l'onglet apparaît sans
 * qu'il ait à recharger — et disparaît si on le lui retire.
 *
 * ⏳ On ne pose RIEN tant que le gate charge. Poser un kiosk sans
 * Conversations puis l'ajouter ferait sauter un onglet sous la souris ; poser
 * l'inverse montrerait une seconde les conversations à quelqu'un qui n'y a
 * pas droit. `CockpitShell` rend un canvas neutre pendant ce temps (il attend
 * déjà `kioskReady`).
 *
 * 🪤 Mais « attendre » se borne. Sans org résolue, `useShiftGate` saute sa
 * query et reste `loading` POUR TOUJOURS : attendre le gate sans distinguer
 * « les orgs chargent » de « il n'y en a aucune » laisserait le chatteur sur
 * un canvas vide sans un mot. Une fois `listMine` répondu, un accountId nul
 * est une réponse, pas une attente — on pose alors le kiosk réduit, et c'est
 * Personnel qui dira « Aucune organisation active ».
 *
 * 🩸 Et « ne rien poser » ne veut pas dire « retirer ce qui est posé ». Le gate
 * ne charge pas qu'au démarrage : toute reconnexion du websocket Convex (veille
 * de la machine, coupure Wi-Fi, redémarrage du backend) jette le cache des
 * queries — `client.ts` reconstruit un `RemoteQuerySet` VIDE à chaque `onOpen`
 * — et `gate.loading` redevient vrai le temps de la transition. Tant que le
 * retrait vivait dans le cleanup de l'effet de pose, il partait à CHAQUE
 * changement de dépendance : le chatteur voyait son écran se vider puis se
 * reconstruire à neuf, sans ses conversations ouvertes. Pire, pendant ce trou
 * `layoutAtom` retombe sur le workspace PERSISTANT — un clic sur une
 * conversation y ouvrait l'onglet, dans un layout qu'un rôle en kiosque ne voit
 * JAMAIS : le clic semblait sans effet. Signalé par Kayla le 10-09-2026 (« des
 * fois je clique sur une discussion, ça ne m'ouvre pas »).
 *
 * Le retrait vit donc dans un effet séparé qui ne dépend QUE du rôle : on ne
 * démonte que si le caller quitte le kiosque (ou au démontage). Un état de
 * chargement ne fait plus rien du tout, et `nextKiosk` ne reconstruit que si
 * les onglets du rôle changent vraiment.
 *
 * ⚠️ Ça ne mordait que les rôles de `KIOSK_SHIFT_GATED_ROLES` : eux seuls font
 * dépendre l'effet de deux queries Convex. Le manager, le partenaire et
 * l'affilié ont `gateKnown` toujours vrai — leur kiosque ne clignotait pas.
 */
export function useKioskLayout(params: {
  /** Le kiosque du caller, ou null s'il garde le Bureau (cf `kioskRoleFor`). */
  kioskRole: KioskRole | null;
  /** true quand les permissions sont résolues (rôle connu). */
  ready: boolean;
}) {
  const setKiosk = useSetAtom(kioskLayoutOverrideAtom);
  const shiftGated =
    params.kioskRole !== null && KIOSK_SHIFT_GATED_ROLES.has(params.kioskRole);
  // Org active = org de session (même résolution que ConversationsPanel).
  const { accountId, isLoading: accountsLoading } = useActiveAccount();
  const gate = useShiftGate(shiftGated ? accountId : null);
  const withConversations = !shiftGated || gate.hasAssignedShift;
  const gateKnown =
    !shiftGated || (!accountsLoading && (accountId === null || !gate.loading));

  // Sortie de kiosque AVÉRÉE — rôle connu, et ce rôle garde le Bureau. Un
  // `kioskRole` nul pendant que les permissions chargent n'est pas une sortie :
  // `usePermissions` est lui aussi une query Convex, et il rend
  // `{roleSlug: null, isLoading: true}` à chaque reconnexion. Sans le garde
  // `ready`, le kiosque tomberait au même blip que celui du shift gate.
  useEffect(() => {
    if (params.ready && !params.kioskRole) setKiosk(null);
  }, [params.ready, params.kioskRole, setKiosk]);

  // Retrait au démontage, et là seulement. Séparé pour que sa fonction de
  // nettoyage ne se rejoue PAS à chaque changement de dépendance — c'était tout
  // le défaut (cf la trappe 🩸 ci-dessus).
  useEffect(() => () => setKiosk(null), [setKiosk]);

  // Pose / mise à jour. Pas de cleanup : ce qui est posé reste posé tant que le
  // caller est en kiosque.
  useEffect(() => {
    if (!params.ready) return;
    const role = params.kioskRole;
    if (!role || !gateKnown) return;
    setKiosk((prev) => nextKiosk(prev, role, { withConversations }));
  }, [params.ready, params.kioskRole, gateKnown, withConversations, setKiosk]);
}

export const layoutAtom = atom(
  (get): Layout => {
    // Overrides in-memory (non-persistants) priment sur le workspace courant :
    //   - wizard : single-Vault pendant l'onboarding créatrice ;
    //   - kiosk  : layout fixe (onglets du rôle, cf KIOSK_TABS).
    // Le wizard prime sur le kiosk (précédence défensive).
    const override =
      get(wizardLayoutOverrideAtom) ?? get(kioskLayoutOverrideAtom);
    if (override) return override;
    const ws = get(activeWorkspaceAtom);
    return ws?.layout ?? emptyLayout();
  },
  (get, set, update: Layout | ((prev: Layout) => Layout)) => {
    // Quand un override (wizard ou kiosk) est actif, les writes (openItemTab du
    // VaultPanel côté wizard ; resize du Split côté kiosk) vont sur l'override
    // et restent in-memory. Au clear, l'user retrouve son workspace intact.
    const wizardOverride = get(wizardLayoutOverrideAtom);
    const override = wizardOverride ?? get(kioskLayoutOverrideAtom);
    if (override) {
      const newLayout =
        typeof update === "function"
          ? (update as (prev: Layout) => Layout)(override)
          : update;
      if (newLayout !== override) {
        set(
          wizardOverride ? wizardLayoutOverrideAtom : kioskLayoutOverrideAtom,
          newLayout,
        );
      }
      return;
    }

    const id = get(activeWorkspaceIdAtom);
    const workspaces = get(workspacesAtom);
    const current = workspaces[id];
    if (!current) return;

    const newLayout =
      typeof update === "function"
        ? (update as (prev: Layout) => Layout)(current.layout)
        : update;
    if (newLayout === current.layout) return;

    // Push pre-mutation snapshot dans l'historique sauf si on est dans
    // un undo/redo en cours (skipHistoryAtom = true).
    if (!get(skipHistoryAtom)) {
      set(historyByWorkspaceAtom, (h) =>
        pushHistorySnapshot(h, id, current.layout)
      );
    }

    set(workspacesAtom, (w) => {
      const cur = w[id];
      if (!cur) return w;
      return {
        ...w,
        [id]: {
          ...cur,
          layout: newLayout,
          updatedAt: Date.now(),
        },
      };
    });
  }
);
